// ListApps.tsx
import { Button, useToaster, Message, Modal } from "rsuite";
import { PanelService } from "network/panel/network";
import { IApp, AppStatus } from "@bprotsyk/aso-core";
import type { ISectionsList } from "@bprotsyk/aso-core/lib/offers/list";
import UpsertAppModal from "ui/modals/apps/app-upsert/upsert-app";
import AppsTable from "./apps-table";
import React, { useEffect, useState, useRef } from "react";
import { createLogger } from '../../../../utils/logger';
import { isPdlOfferwallListApp } from './pdl-app-list-policy';
import "./_apps.scss";

const logger = createLogger('apps');

// Function to get status text interpretation
const getStatusText = (status: AppStatus): string => {
  switch (status) {
    case AppStatus.ARCHIVED:
      return "Архів";
    case AppStatus.IN_DEVELOPMENT:
      return "В розробці";
    case AppStatus.READY:
      return "Готовий";
    default:
      return "Невідомо";
  }
};

// Filter interface and local storage key
interface IFilters {
  hideArchived: boolean;
  hideInDevelopment: boolean;
}

const FILTERS_STORAGE_KEY = 'apps-filters';

// Load filters from localStorage
const loadFilters = (): IFilters => {
  try {
    const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    logger.error('Error loading filters from localStorage', error);
  }
  return {
    hideArchived: false,
    hideInDevelopment: false,
  };
};

// Header height constants
const HEADER_HEIGHT = 56; // Main header height
const SUB_HEADER_HEIGHT = 64; // Sub-header height
const TOTAL_HEADER_HEIGHT = HEADER_HEIGHT + SUB_HEADER_HEIGHT;
const Apps: React.FC = () => {
  const [apps, setApps] = useState<IApp[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedApp, setSelectedApp] = useState<IApp | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [tableHeight, setTableHeight] = useState(window.innerHeight - TOTAL_HEADER_HEIGHT);
  const [saving, setSaving] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ x: number; y: number } | null>(null);
  const dropdownRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  
  // Toast notification
  const toaster = useToaster();
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortType, setSortType] = useState<'asc' | 'desc'>('asc');
  
  // Filters state
  const [filters] = useState<IFilters>(loadFilters());
  const [sectionsLists, setSectionsLists] = useState<ISectionsList[]>([]);

  useEffect(() => {
    const handleResize = () => {
      setTableHeight(window.innerHeight - TOTAL_HEADER_HEIGHT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch apps on component mount and subscribe to updates
  useEffect(() => {
    logger.info('Apps component mounted, subscribing to updates');
    
    // Початкове завантаження даних
    fetchApps();
    
    // Preload sections lists for modal
    PanelService.getSectionsLists().then((res) => {
      if (res) setSectionsLists(res);
    }).catch(console.error);
    
    // Підписуємось на оновлення в реальному часі
    const unsubscribe = PanelService.subscribeToAppsUpdates((updatedApps) => {
      if (!updatedApps || updatedApps.length === 0) return;

      setApps((prev) => {
        if (prev.length === 0) return updatedApps;

        const updated = [...prev];
        for (const incoming of updatedApps) {
          const idx = updated.findIndex((a) => a.id === incoming.id);
          if (idx >= 0) {
            updated[idx] = incoming;
          } else {
            updated.push(incoming);
          }
        }
        return updated;
      });
    });
    
    // Відписуємось при розмонтуванні компонента
    return () => {
      logger.info('Apps component unmounted, unsubscribing from updates');
      unsubscribe();
    };
  }, []);

  // Fetch apps from the server
  const fetchApps = async () => {
    logger.info('Fetching app list...');
    setLoading(true);
    try {
      const data = await PanelService.getApps();
      logger.info('App list received', data ? (Array.isArray(data) ? data.length : 'object') : 'no data');
      
      if (Array.isArray(data)) {
        setApps(data);
      } else if (data && typeof data === 'object') {
        setApps(Object.values(data));
      } else {
        logger.error('Unexpected data format', data);
        setApps([]);
      }
    } catch (error) {
      logger.error('Error fetching app list', error);
      setApps([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handleAppClick = (app: IApp) => {
    setSelectedApp(app);
  };

  const handleModalClose = () => {
    setSelectedApp(null);
    fetchApps();
  };

  // Handle status cell click
  const handleStatusCellClick = (appId: number, event: React.MouseEvent) => {
    if (openDropdownId === appId) {
      setOpenDropdownId(null);
      setDropdownPosition(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      setDropdownPosition({
        x: rect.left,
        y: rect.top + window.scrollY
      });
      setOpenDropdownId(appId);
    }
  };

  // State for status change confirmation dialog
  const [statusChangeDialog, setStatusChangeDialog] = useState<{
    appId: number;
    appName: string;
    currentStatus: AppStatus;
    newStatus: AppStatus;
  } | null>(null);

  // Handle status change with confirmation
  const handleStatusChange = (appId: number, newStatus: AppStatus) => {
    const app = apps.find(a => a.id === appId);
    if (!app) return;

    setStatusChangeDialog({
      appId,
      appName: app.name,
      currentStatus: app.status,
      newStatus
    });
    
    setOpenDropdownId(null);
    setDropdownPosition(null);
  };

  // Confirm and save status change
  const confirmStatusChange = async () => {
    if (!statusChangeDialog) return;

    setSaving(true);
    try {
      await PanelService.updateAppStatuses([
        { appId: statusChangeDialog.appId, status: statusChangeDialog.newStatus }
      ]);
      
      toaster.push(
        <Message showIcon type="success" closable={false}>
          Статус успішно оновлено
        </Message>,
        { placement: 'topCenter', duration: 2000 }
      );
      
      await fetchApps();
    } catch (error) {
      logger.error('Error saving status', error);
      
      toaster.push(
        <Message showIcon type="error" closable={false}>
          Помилка при оновленні статусу
        </Message>,
        { placement: 'topCenter', duration: 3000 }
      );
    } finally {
      setSaving(false);
      setStatusChangeDialog(null);
    }
  };


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdownId !== null) {
        const dropdownElement = dropdownRefs.current.get(openDropdownId);
        const clickedElement = event.target as HTMLElement;
        
        if (clickedElement.closest('.status-cell')) {
          return;
        }
        
        if (dropdownElement && !dropdownElement.contains(clickedElement)) {
          setOpenDropdownId(null);
          setDropdownPosition(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  // Filter and sort apps
  const offerwallCount = apps.filter(isPdlOfferwallListApp).length;

  const filteredApps = apps
    .filter((app) => {
      if (!isPdlOfferwallListApp(app)) return false;
      
      const searchLower = search.toLowerCase();
      const appIdString = `#${app.id}`;
      const appName = app.name?.toLowerCase() || '';
      const bundle = app.bundle?.toLowerCase() || '';
      const domain = app.domainParams?.name?.toLowerCase() || '';
      
      const matchesSearch = appIdString.includes(searchLower) ||
        appName.includes(searchLower) ||
        bundle.includes(searchLower) ||
        domain.includes(searchLower);
      
      if (!matchesSearch) return false;
      
      if (filters.hideArchived && app.status === AppStatus.ARCHIVED) return false;
      if (filters.hideInDevelopment && app.status === AppStatus.IN_DEVELOPMENT) return false;
      
      return true;
    })
    .sort((a, b) => {
      if (!sortColumn) return 0;
      
      let aValue: any = '';
      let bValue: any = '';
      
      switch (sortColumn) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'bundle':
          aValue = (a.bundle || '').toLowerCase();
          bValue = (b.bundle || '').toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        // integrationVersion case removed - field no longer exists
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortType === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortType === 'asc' ? 1 : -1;
      return 0;
    });

  // Handle sorting
  const handleSort = (dataKey: string, sortType: 'asc' | 'desc' | undefined) => {
    setSortColumn(dataKey);
    setSortType(sortType || 'asc');
  };

  return (
    <div className="apps">
      <div className="apps-content">
        <AppsTable
          apps={filteredApps}
          loading={loading}
          onAppClick={handleAppClick}
          onStatusChange={handleStatusChange}
          openDropdownId={openDropdownId}
          dropdownPosition={dropdownPosition}
          onStatusCellClick={handleStatusCellClick}
          dropdownRefs={dropdownRefs}
          sortColumn={sortColumn}
          sortType={sortType}
          onSort={handleSort}
          tableHeight={Math.max(tableHeight, 320)}
          search={search}
          onSearchChange={handleSearchChange}
          onCreateApp={() => setCreateModalOpen(true)}
          filters={filters}
          offerwallCount={offerwallCount}
        />
        {(selectedApp || createModalOpen) && (
          <UpsertAppModal
            onClose={() => {
              setSelectedApp(null);
              setCreateModalOpen(false);
              handleModalClose();
            }}
            app={selectedApp || undefined}
            create={createModalOpen}
            preloadedSectionsLists={sectionsLists}
            // selectedIntegration={selectedVersionModal} // IntegrationVersion removed
          />
        )}

        {/* Status Change Confirmation Dialog */}
        <Modal
          open={statusChangeDialog !== null}
          onClose={() => setStatusChangeDialog(null)}
          size="xs"
        >
          <Modal.Header>
            <Modal.Title>Підтвердження зміни статусу</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {statusChangeDialog && (
              <div style={{ padding: '12px 0' }}>
                <div style={{ marginBottom: '12px', color: '#ecf0f1' }}>
                  <strong>ID:</strong> #{statusChangeDialog.appId}
                </div>
                <div style={{ marginBottom: '16px', color: '#ecf0f1' }}>
                  <strong>Назва:</strong> {statusChangeDialog.appName}
                </div>
                <div style={{ color: '#ecf0f1', lineHeight: '1.6' }}>
                  Змінити статус з <strong>"{getStatusText(statusChangeDialog.currentStatus)}"</strong> на <strong>"{getStatusText(statusChangeDialog.newStatus)}"</strong>?
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => setStatusChangeDialog(null)} appearance="subtle">
              Скасувати
            </Button>
            <Button onClick={confirmStatusChange} appearance="primary" loading={saving}>
              Підтвердити
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default Apps;
